import type { DynamicClient } from '@dynamic-labs-sdk/client';
import { emitEvent, getCore, getDefaultClient } from '@dynamic-labs-sdk/client/core';

import { NoPendingPhantomRequestError } from '../errors/NoPendingPhantomRequestError';
import { NoPhantomSessionError } from '../errors/NoPhantomSessionError';
import { PhantomMissingEncryptionParamsError } from '../errors/PhantomMissingEncryptionParamsError';
import { PhantomRedirectRejectedError } from '../errors/PhantomRedirectRejectedError';
import type {
  PhantomBroadcastMessage,
  PhantomEvents,
} from '../phantomRedirect.broadcast.types';
import type {
  PhantomConnectResponse,
  PhantomSignAllTransactionsResponse,
  PhantomSignAndSendTransactionResponse,
  PhantomSignMessageResponse,
  PhantomSignTransactionResponse,
} from '../PhantomRedirectWalletProvider.types';
import { pendingRequestStorageKeySchema } from '../storage/pendingRequestStorageSchema';
import { phantomSessionStorageKeySchema } from '../storage/phantomSessionStorageSchema';
import { broadcastPhantomEvent } from '../utils/broadcastPhantomEvent';
import { createNaClSharedSecret } from '../utils/crypto/createNaClSharedSecret';
import { decryptPayload } from '../utils/crypto/decryptPayload';
import { getPhantomOriginTab } from '../utils/getPhantomOriginTab';
import { parsePhantomRedirectParams } from '../utils/parsePhantomRedirectParams';

const methodErrorEventMap: Record<string, keyof PhantomEvents> = {
  connect: 'phantomRedirectConnectionError',
  disconnect: 'phantomRedirectDisconnectError',
  signAllTransactions: 'phantomRedirectSignAllTransactionsError',
  signAndSendTransaction: 'phantomRedirectSignAndSendTransactionError',
  signMessage: 'phantomRedirectSignMessageError',
  signTransaction: 'phantomRedirectSignTransactionError',
};

export type CompletePhantomRedirectParams = {
  url: URL;
};

/**
 * Returns true when the current JavaScript context is a **clone tab** —
 * a new browser tab that Phantom opened to deliver the redirect callback on
 * mobile browsers.
 *
 * ## Detection mechanism
 *
 * When a wallet operation is initiated (`connect`, `signMessage`, etc.)
 * `setupPhantomListeners` sets `isOriginTab = true` on the per-client
 * `phantomOriginTabService`. Because each browser tab (and each native mobile
 * context) has its own JavaScript heap, the clone tab's client starts with
 * `isOriginTab = false` and will never have it set to `true`.
 *
 * - `isOriginTab === false` → clone tab (mobile browser redirect) → should close
 * - `isOriginTab === true` → originating tab or native mobile context → should stay open
 */
const isCloneTab = (client: DynamicClient): boolean =>
  !getPhantomOriginTab(client).isOriginTab;

/**
 * Removes the pending request from storage, emits a Phantom redirect event to
 * the local client, broadcasts it to the originating tab via `BroadcastChannel`,
 * and — when running in a clone tab — emits `phantomRedirectCloseTab` so that
 * the customer's `onCloseTab` callback can close the tab.
 *
 * This is the single point of truth for all cleanup and event delivery in the
 * redirect flow. Every code path calls this exactly once, ensuring a consistent
 * ordering: remove pending request → emit → broadcast → (maybe) close tab.
 */
const completeAndEmitPhantomRedirectEvent = async <
  K extends keyof PhantomEvents
>(
  { args, event }: PhantomBroadcastMessage<K>,
  client: DynamicClient
): Promise<void> => {
  const core = getCore(client);
  const cloneTab = isCloneTab(client);

  await core.storage.removeItem(pendingRequestStorageKeySchema);

  emitEvent({ args, event }, client);

  core.logger.debug('[PHANTOM] completeAndEmitPhantomRedirectEvent: broadcasting', { event });
  broadcastPhantomEvent({ args, event }, client);

  if (cloneTab) {
    core.logger.debug('[PHANTOM] completeAndEmitPhantomRedirectEvent: emitting closeTab (clone tab)');
    emitEvent({ args: {}, event: 'phantomRedirectCloseTab' }, client);
  }
};

/**
 * Completes the Phantom wallet redirect flow after the deep link callback.
 *
 * This function is called on the page that Phantom redirects back to after the
 * user approves or rejects a wallet operation. It processes the redirect URL,
 * decrypts the response, updates session storage, and emits the appropriate
 * events so that waiting callers (in this tab or the originating tab) can
 * resolve or reject their pending promises.
 *
 * ## Cross-tab delivery
 *
 * On mobile browsers, Phantom opens the redirect URL in a **new tab** (the
 * clone tab). Events are delivered to the originating tab via the
 * `BroadcastChannel` API. Once delivery is complete this function emits
 * `phantomRedirectCloseTab` so that the clone tab can be closed and the user
 * is returned to the originating tab. See the `phantomRedirectCloseTab` event
 * JSDoc for details on how to handle tab closing.
 *
 * @param params.url - The callback URL received from Phantom
 * @param client - The Dynamic client instance
 * @throws {NoPendingPhantomRequestError} If no pending request is found in storage
 * @throws {PhantomMissingEncryptionParamsError} If the URL is missing the
 *   required encryption parameters (`phantom_encryption_public_key`, `data`,
 *   `nonce`) and does not carry an error code either
 * @throws {PhantomRedirectRejectedError} If the user rejected the request in Phantom
 */
export const completePhantomRedirect = async (
  { url }: CompletePhantomRedirectParams,
  client: DynamicClient = getDefaultClient()
): Promise<void> => {
  const core = getCore(client);

  const params = parsePhantomRedirectParams({ url });

  const pendingRequest = await core.storage.getItem(
    pendingRequestStorageKeySchema
  );

  if (!pendingRequest) {
    throw new NoPendingPhantomRequestError();
  }

  if (params.errorCode) {
    const error = new PhantomRedirectRejectedError({
      errorCode: params.errorCode,
      errorMessage: params.errorMessage,
    });

    const event = methodErrorEventMap[pendingRequest.method];

    await completeAndEmitPhantomRedirectEvent(
      { args: { error }, event },
      client
    );

    return;
  }

  // Disconnect success: Phantom returns an empty callback URL (no data, nonce,
  // or phantom_encryption_public_key). Handle it before the data/nonce guard
  // so we don't throw PhantomMissingEncryptionParamsError for a valid response.
  if (pendingRequest.method === 'disconnect') {
    await completeAndEmitPhantomRedirectEvent(
      { args: {}, event: 'phantomRedirectDisconnectComplete' },
      client
    );

    return;
  }

  if (!params.data || !params.nonce) {
    await core.storage.removeItem(pendingRequestStorageKeySchema);
    throw new PhantomMissingEncryptionParamsError();
  }

  let sharedSecret: string;

  if (pendingRequest.method === 'connect') {
    // The connect response includes phantom_encryption_public_key so we can
    // establish the shared secret for the first time.
    if (!params.phantomPublicKey) {
      throw new PhantomMissingEncryptionParamsError();
    }

    sharedSecret = createNaClSharedSecret({
      ourSecretKey: pendingRequest.dappSecretKey,
      theirPublicKey: params.phantomPublicKey,
    });
  } else {
    // Sign callbacks (signMessage, signTransaction, etc.) do NOT include
    // phantom_encryption_public_key — the session is already established.
    // Use the shared secret stored during connect.
    const session = await core.storage.getItem(phantomSessionStorageKeySchema);

    if (!session) {
      throw new NoPhantomSessionError();
    }

    sharedSecret = session.sharedSecret;
  }

  const decryptedData = decryptPayload({
    data: params.data,
    nonce: params.nonce,
    sharedSecret,
  });

  if (pendingRequest.method === 'connect') {
    const connectData = decryptedData as PhantomConnectResponse;

    await core.storage.setItem(phantomSessionStorageKeySchema, {
      dappPublicKey: pendingRequest.dappPublicKey,
      dappSecretKey: pendingRequest.dappSecretKey,
      phantomPublicKey: params.phantomPublicKey,
      sessionToken: connectData.session,
      sharedSecret,
      walletAddress: connectData.public_key,
    });

    await completeAndEmitPhantomRedirectEvent(
      {
        args: {
          address: connectData.public_key,
          publicKey: connectData.public_key,
        },
        event: 'phantomRedirectConnectionComplete',
      },
      client
    );
  } else if (pendingRequest.method === 'signMessage') {
    const signData = decryptedData as PhantomSignMessageResponse;
    await completeAndEmitPhantomRedirectEvent(
      {
        args: {
          message: pendingRequest.message,
          signature: signData.signature,
        },
        event: 'phantomRedirectSignMessageComplete',
      },
      client
    );
  } else if (pendingRequest.method === 'signTransaction') {
    const signData = decryptedData as PhantomSignTransactionResponse;
    await completeAndEmitPhantomRedirectEvent(
      {
        args: { transaction: signData.transaction },
        event: 'phantomRedirectSignTransactionComplete',
      },
      client
    );
  } else if (pendingRequest.method === 'signAllTransactions') {
    const signData = decryptedData as PhantomSignAllTransactionsResponse;
    await completeAndEmitPhantomRedirectEvent(
      {
        args: { transactions: signData.transactions },
        event: 'phantomRedirectSignAllTransactionsComplete',
      },
      client
    );
  } else {
    const signData = decryptedData as PhantomSignAndSendTransactionResponse;
    await completeAndEmitPhantomRedirectEvent(
      {
        args: { signature: signData.signature },
        event: 'phantomRedirectSignAndSendTransactionComplete',
      },
      client
    );
  }
};
